import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// Layouts
import LandingLayout from '@/layouts/LandingLayout.vue';

// Landing Views
import WelcomeView from '@/views/landing/WelcomeView.vue';
import LoginView from '@/views/landing/LoginView.vue';
import SignupView from '@/views/landing/SignupView.vue';

// Application Views
const AppLayout = () => import('@/layouts/AppLayout.vue');
const ManuscriptPickerView = () => import('@/views/ManuscriptPickerView.vue');
const ManuscriptView = () => import('@/views/ManuscriptView.vue');

const routes = [
  // ==========================================
  // LANDING PAGES (Public)
  // ==========================================
  {
    path: '/',
    component: LandingLayout,
    children: [
      {
        path: '',
        name: 'Welcome',
        component: WelcomeView,
        meta: {
          title: 'Welcome - NovelWriter2',
          public: true
        }
      },
      {
        path: 'login',
        name: 'Login',
        component: LoginView,
        meta: {
          title: 'Login - NovelWriter2',
          public: true,
          guestOnly: true
        }
      },
      {
        path: 'signup',
        name: 'Signup',
        component: SignupView,
        meta: {
          title: 'Sign Up - NovelWriter2',
          public: true,
          guestOnly: true
        }
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
        meta: {
          title: 'Forgot Password - NovelWriter2',
          public: true,
          guestOnly: true
        }
      },
      {
        path: 'reset-password',
        name: 'ResetPassword',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: {
          title: 'Reset Password - NovelWriter2',
          public: true
        }
      },
      // Placeholder routes for future landing pages
      {
        path: 'features',
        name: 'Features',
        component: () => import('@/views/landing/FeaturesView.vue'),
        meta: {
          title: 'Features - NovelWriter2',
          public: true
        }
      },
      {
        path: 'pricing',
        name: 'Pricing',
        component: () => import('@/views/landing/PricingView.vue'),
        meta: {
          title: 'Pricing - NovelWriter2',
          public: true
        }
      }
    ]
  },

  // ==========================================
  // AUTH CALLBACK PAGES (No Layout)
  // ==========================================
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/auth/AuthCallbackView.vue'),
    meta: {
      title: 'Signing In... - NovelWriter2',
      public: true
    }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: {
      title: 'Verify Email - NovelWriter2',
      public: true
    }
  },

  // ==========================================
  // MAIN APPLICATION (Protected)
  // ==========================================
  {
    path: '/dashboard',
    redirect: '/app',
    meta: { requiresAuth: true }
  },
  {
    path: '/app',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'ManuscriptPicker',
        component: ManuscriptPickerView,
        meta: {
          title: 'My Manuscripts',
          requiresAuth: true
        }
      },
      {
        path: 'manuscript',
        name: 'Manuscript',
        component: ManuscriptView,
        meta: { title: 'Manuscript - NovelWriter2' }
      },
      // Placeholder routes for future app views
      {
        path: 'characters',
        name: 'Characters',
        component: () => import('@/views/CharactersView.vue'),
        meta: { title: 'Characters - NovelWriter2' }
      },
      {
        path: 'world',
        name: 'World',
        component: () => import('@/views/WorldView.vue'),
        meta: { title: 'World - NovelWriter2' }
      },
      {
        path: 'plot',
        name: 'Plot',
        component: () => import('@/views/PlotView.vue'),
        meta: { title: 'Plot - NovelWriter2' }
      },
      {
        path: 'story-elements',
        name: 'StoryElements',
        component: () => import('@/views/StoryElementsView.vue'),
        meta: { title: 'Story Elements - NovelWriter2' }
      },
      {
        path: 'notes',
        name: 'Notes',
        component: () => import('@/views/NotesView.vue'),
        meta: { title: 'Notes - NovelWriter2' }
      },
      {
        path: 'writing-style',
        name: 'WritingStyle',
        component: () => import('@/views/WritingStyleView.vue'),
        meta: { title: 'Writing Style - NovelWriter2' }
      },
      {
        path: 'sections',
        name: 'SectionsLibrary',
        component: () => import('@/views/SectionsLibraryView.vue'),
        meta: { title: 'Sections Library - NovelWriter2' }
      }
    ]
  },

  // ==========================================
  // USER PROFILE & SETTINGS (Protected)
  // ==========================================
  {
    path: '/profile',
    name: 'ProfileSettings',
    component: () => import('@/views/profile/ProfileSettingsView.vue'),
    meta: {
      title: 'Profile Settings - NovelWriter2',
      requiresAuth: true
    }
  },
  {
    path: '/subscription',
    name: 'Subscription',
    component: () => import('@/views/profile/SubscriptionView.vue'),
    meta: {
      title: 'Subscription & Billing - NovelWriter2',
      requiresAuth: true
    }
  },

  // ==========================================
  // ERROR PAGES
  // ==========================================
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: '404 - Page Not Found - NovelWriter2'
    }
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  }
});

// ==========================================
// NAVIGATION GUARDS
// ==========================================

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title || 'NovelWriter2';

  const authStore = useAuthStore();
  const isAuthCallbackRoute = to.path === '/auth/callback';

  if (isAuthCallbackRoute && authStore.initializing) {
    next();
    return;
  }

  await authStore.initialize();

  if (isAuthCallbackRoute) {
    next();
    return;
  }

  const isAuthenticated = authStore.isAuthenticated;
  const isRecoverySession = authStore.isRecoverySession;

  if (isRecoverySession && to.meta.requiresAuth) {
    next({ name: 'ResetPassword' });
    return;
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    next({
      name: 'Login',
      query: { redirect: to.fullPath }
    });
    return;
  }

  if (to.meta.guestOnly && isAuthenticated) {
    const redirectTarget =
      typeof to.query.redirect === 'string' ? to.query.redirect : '/app';
    next({ path: redirectTarget });
    return;
  }

  next();
});

export default router;
